import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useExerciseBookmarks } from '@/hooks/useExerciseBookmarks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Bookmark, FolderPlus, Trash2, Pencil, Check, X,
  Dumbbell, FolderOpen, BookmarkCheck, MoreHorizontal,
  CheckSquare, Square, MinusSquare, GripVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';

function SortableBookmarkRow({
  bm, selecting, isSelected, onToggleSelect,
  collectionId, allCollections, onMoveBookmark,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: bm.id, disabled: selecting });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? 'relative' : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-2 rounded-md group transition-colors ${
        isDragging
          ? 'bg-muted shadow-md ring-1 ring-border'
          : selecting
            ? isSelected ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/40 cursor-pointer'
            : 'hover:bg-muted/40'
      }`}
      onClick={selecting ? () => onToggleSelect(bm.id) : undefined}
    >
      {selecting ? (
        <button
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          onClick={e => { e.stopPropagation(); onToggleSelect(bm.id); }}
        >
          {isSelected ? (
            <CheckSquare className="h-4 w-4 text-primary" />
          ) : (
            <Square className="h-4 w-4" />
          )}
        </button>
      ) : (
        <button
          className="shrink-0 text-muted-foreground/40 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <div className="h-8 w-8 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
        {bm.exercises?.video_thumbnail_url ? (
          <img src={bm.exercises.video_thumbnail_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <Dumbbell className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </div>
      {selecting ? (
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{bm.exercises?.name || 'Unknown Exercise'}</p>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            {bm.exercises?.difficulty && <span>{bm.exercises.difficulty}</span>}
            {bm.exercises?.primary_region && <span>{bm.exercises.primary_region}</span>}
          </div>
        </div>
      ) : (
        <Link to={`/exercise/${bm.exercise_id}`} className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{bm.exercises?.name || 'Unknown Exercise'}</p>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            {bm.exercises?.difficulty && <span>{bm.exercises.difficulty}</span>}
            {bm.exercises?.primary_region && <span>{bm.exercises.primary_region}</span>}
          </div>
        </Link>
      )}
      {!selecting && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="xs" className="opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {allCollections.filter(c => c.id !== collectionId).map(c => (
              <DropdownMenuItem key={c.id} onClick={() => onMoveBookmark(bm.id, c.id)}>
                <FolderOpen className="h-3 w-3 mr-2" />
                <span className="text-xs">Move to {c.name}</span>
              </DropdownMenuItem>
            ))}
            {collectionId && (
              <DropdownMenuItem onClick={() => onMoveBookmark(bm.id, null)}>
                <Bookmark className="h-3 w-3 mr-2" />
                <span className="text-xs">Uncategorize</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function CollectionCard({
  collection, bookmarks, onDelete, onRename, onMoveBookmark,
  allCollections, selecting, selectedIds, onToggleSelect, onToggleAll,
  onReorder,
}) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(collection?.name || '');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleRename = () => {
    if (editName.trim() && collection) {
      onRename(collection.id, editName.trim());
    }
    setEditing(false);
  };

  const title = collection ? collection.name : 'Uncategorized';
  const id = collection?.id || null;

  const allSelected = bookmarks.length > 0 && bookmarks.every(bm => selectedIds.has(bm.id));
  const someSelected = bookmarks.some(bm => selectedIds.has(bm.id));

  const bookmarkIds = useMemo(() => bookmarks.map(bm => bm.id), [bookmarks]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = bookmarkIds.indexOf(active.id);
    const newIndex = bookmarkIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(bookmarkIds, oldIndex, newIndex);
    onReorder(newOrder);
  };

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          {editing ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className="h-7 text-sm"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditing(false); }}
              />
              <Button size="xs" variant="ghost" onClick={handleRename}><Check className="h-3.5 w-3.5" /></Button>
              <Button size="xs" variant="ghost" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5" /></Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                {selecting && bookmarks.length > 0 && (
                  <button
                    onClick={() => onToggleAll(bookmarks.map(bm => bm.id))}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {allSelected ? (
                      <CheckSquare className="h-4 w-4 text-primary" />
                    ) : someSelected ? (
                      <MinusSquare className="h-4 w-4 text-primary" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                )}
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm">{title}</CardTitle>
                <Badge variant="secondary" className="text-[10px]">{bookmarks.length}</Badge>
              </div>
              {collection && !selecting && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="xs"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { setEditName(collection.name); setEditing(true); }}>
                      <Pencil className="h-3.5 w-3.5 mr-2" />Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-destructive" onClick={() => onDelete(collection.id)}>
                      <Trash2 className="h-3.5 w-3.5 mr-2" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </div>
        {collection?.description && (
          <CardDescription className="text-xs mt-1">{collection.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {bookmarks.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">No bookmarks in this collection</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext items={bookmarkIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {bookmarks.map(bm => (
                  <SortableBookmarkRow
                    key={bm.id}
                    bm={bm}
                    selecting={selecting}
                    isSelected={selectedIds.has(bm.id)}
                    onToggleSelect={onToggleSelect}
                    collectionId={id}
                    allCollections={allCollections}
                    onMoveBookmark={onMoveBookmark}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </CardContent>
    </Card>
  );
}

const Bookmarks = () => {
  const {
    bookmarks, collections, loading,
    deleteCollection, renameCollection,
    createCollection, moveToCollection, removeBookmarks, reorderBookmarks,
  } = useExerciseBookmarks();

  const [newCollectionName, setNewCollectionName] = useState('');
  const [creating, setCreating] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [removing, setRemoving] = useState(false);

  const handleCreate = async () => {
    if (!newCollectionName.trim()) return;
    await createCollection(newCollectionName.trim());
    setNewCollectionName('');
    setCreating(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (ids) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const selectAllBookmarks = () => {
    setSelectedIds(new Set(bookmarks.map(b => b.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const exitSelection = () => {
    setSelecting(false);
    setSelectedIds(new Set());
  };

  const handleRemoveSelected = async () => {
    if (selectedIds.size === 0) return;
    setRemoving(true);
    const success = await removeBookmarks([...selectedIds]);
    setRemoving(false);
    if (success) {
      setSelectedIds(new Set());
      if (bookmarks.length - selectedIds.size === 0) {
        setSelecting(false);
      }
    }
  };

  const uncategorized = bookmarks.filter(b => !b.collection_id);
  const grouped = collections.map(c => ({
    collection: c,
    bookmarks: bookmarks.filter(b => b.collection_id === c.id),
  }));

  const allSelected = bookmarks.length > 0 && selectedIds.size === bookmarks.length;

  return (
    <>
      <Helmet><title>Bookmarks - My Movement Space</title></Helmet>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Exercise Bookmarks</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {bookmarks.length} bookmarked exercise{bookmarks.length !== 1 ? 's' : ''} across {collections.length + 1} collection{collections.length !== 0 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!selecting && !creating && bookmarks.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setSelecting(true)}>
                <CheckSquare className="h-4 w-4 mr-2" />Select
              </Button>
            )}
            {creating ? (
              <>
                <Input
                  value={newCollectionName}
                  onChange={e => setNewCollectionName(e.target.value)}
                  placeholder="Collection name..."
                  className="h-8 w-48 text-sm"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }}
                />
                <Button size="sm" onClick={handleCreate} disabled={!newCollectionName.trim()}>Create</Button>
                <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
              </>
            ) : !selecting && (
              <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
                <FolderPlus className="h-4 w-4 mr-2" />New Collection
              </Button>
            )}
          </div>
        </div>

        {selecting && (
          <div className="flex items-center justify-between bg-muted/50 border rounded-lg px-4 py-2.5">
            <div className="flex items-center gap-3">
              <button
                onClick={allSelected ? deselectAll : selectAllBookmarks}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {allSelected ? (
                  <CheckSquare className="h-4 w-4 text-primary" />
                ) : selectedIds.size > 0 ? (
                  <MinusSquare className="h-4 w-4 text-primary" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
              </button>
              <span className="text-sm text-muted-foreground">
                {selectedIds.size === 0
                  ? 'Select bookmarks to remove'
                  : `${selectedIds.size} of ${bookmarks.length} selected`}
              </span>
              {selectedIds.size > 0 && !allSelected && (
                <Button size="xs" variant="ghost" className="text-xs h-6" onClick={selectAllBookmarks}>
                  Select all
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedIds.size > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleRemoveSelected}
                  disabled={removing}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  {removing ? 'Removing...' : `Remove ${selectedIds.size}`}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={exitSelection}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[0, 1].map(i => (
              <Card key={i} className="shadow-none">
                <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
                <CardContent className="space-y-2">
                  {[0, 1, 2].map(j => <Skeleton key={j} className="h-10 w-full" />)}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : bookmarks.length === 0 ? (
          <Card className="shadow-none">
            <CardContent className="py-16 text-center">
              <BookmarkCheck className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">No bookmarks yet</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">
                Bookmark exercises from the exercise library to organize them here.
              </p>
              <Link to="/exercises">
                <Button variant="outline" size="sm">Browse Exercises</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {grouped.map(({ collection, bookmarks: bms }) => (
              <CollectionCard
                key={collection.id}
                collection={collection}
                bookmarks={bms}
                onDelete={deleteCollection}
                onRename={renameCollection}
                onMoveBookmark={moveToCollection}
                allCollections={collections}
                selecting={selecting}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleAll={toggleAll}
                onReorder={reorderBookmarks}
              />
            ))}
            <CollectionCard
              collection={null}
              bookmarks={uncategorized}
              onDelete={() => {}}
              onRename={() => {}}
              onMoveBookmark={moveToCollection}
              allCollections={collections}
              selecting={selecting}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleAll}
              onReorder={reorderBookmarks}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Bookmarks;
